import { useQuery } from '@tanstack/react-query';
import { ProgressInfo } from '../components/ProgressBar';
import { adminApi } from '../services/adminApi';

export interface UploadProgressResponse {
  uploadId: string;
  progress: ProgressInfo | null;
  session: {
    createdAt: string;
    startedAt?: string;
    retryCount: number;
    error?: string;
  } | null;
}

export function useUploadProgress(uploadId: string, status: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['upload-progress', uploadId],
    queryFn: async (): Promise<UploadProgressResponse> => {
      const response = await adminApi.getUploadProgress(uploadId);
      return {
        uploadId: response.uploadId,
        progress: response.progress,
        session: response.session
      };
    },
    enabled: enabled && status === 'processing',
    refetchInterval: status === 'processing' ? 2000 : false, // Poll every 2 seconds for processing uploads
    refetchIntervalInBackground: false,
    staleTime: 1000, // Consider data stale after 1 second
  });
}

export function useMultipleUploadProgress(uploads: { id: string; status: string }[]) {
  const processingUploads = uploads.filter(upload => upload.status === 'processing');
  
  return useQuery({
    queryKey: ['multiple-upload-progress', processingUploads.map(u => u.id).join(',')],
    queryFn: async (): Promise<Record<string, UploadProgressResponse>> => {
      if (processingUploads.length === 0) {
        return {};
      }

      const responses = await Promise.allSettled(
        processingUploads.map(async (upload) => {
          const response = await adminApi.getUploadProgress(upload.id);
          return { uploadId: upload.id, data: response };
        })
      );

      const result: Record<string, UploadProgressResponse> = {};
      responses.forEach((response, index) => {
        if (response.status === 'fulfilled') {
          result[response.value.uploadId] = response.value.data;
        } else {
          // For failed requests, provide empty progress
          result[processingUploads[index].id] = {
            uploadId: processingUploads[index].id,
            progress: null,
            session: null,
          };
        }
      });

      return result;
    },
    enabled: processingUploads.length > 0,
    refetchInterval: processingUploads.length > 0 ? 3000 : false, // Poll every 3 seconds for multiple uploads
    refetchIntervalInBackground: false,
    staleTime: 2000,
  });
}